import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FullPageStatus } from '../../components/FullPageStatus';
import { PageHeader } from '../../components/PageHeader';
import { Upsell } from '../../components/plan/Upsell';
import { useBrandAllowance } from '../../components/plan/useBrandAllowance';
import { newBrandDraftKey, onboardingDraftKey, restoreDraft } from '../../lib/draftStorage';
import type { OnboardingParseRead } from '../../types/api';
import { useAuth } from '../auth/useAuth';
import { BrandBriefForm, type BriefFlow } from './BrandBriefForm';
import { EMPTY_BRIEF, emptyBrief, fromParsedFields, type BrandBriefValues } from './brandBrief';
import { isBrandKind, type BrandKind } from './brandKind';
import { ChoiceStep } from './ChoiceStep';
import { KindStep } from './KindStep';
import { PasteBriefStep } from './PasteBriefStep';
import { useAccount } from './useAccount';

type Mode = 'kind' | 'choice' | 'paste' | 'form';

interface FormOpening {
  initial: BrandBriefValues;
  unmatched: readonly string[];
  warnings: readonly string[];
  restoredAt: string | null;
  /** Bumped on every fresh opening so the form remounts with new `initial`. */
  generation: number;
}

interface BrandFlowProps {
  flow: BriefFlow;
  /** The form with nothing applied: the empty brief of the chosen kind. */
  pristine: BrandBriefValues;
  /** Null until the session's user id is known, which disables autosave. */
  draftKey: string | null;
  /** The add-a-brand path can be abandoned; the first-run path cannot. */
  onCancel?: () => void;
}

/**
 * Kind -> paste or by hand -> the one form. Both roads end on `BrandBriefForm`
 * with the kind already decided; the kind rides in the draft, so a recovered
 * draft skips straight to the form of the kind it was written for.
 */
function BrandFlow({ flow, pristine, draftKey, onCancel }: BrandFlowProps) {
  // Read once, during the first render: a recovered draft skips the choice
  // screens and opens the form with the user's work already on it.
  const [restored] = useState(() => restoreDraft(draftKey, EMPTY_BRIEF));
  const restoredKind: BrandKind | null =
    restored.savedAt !== null && isBrandKind(restored.value.kind) ? restored.value.kind : null;

  const [kind, setKind] = useState<BrandKind>(restoredKind ?? pristine.kind);
  const [mode, setMode] = useState<Mode>(restoredKind !== null ? 'form' : 'kind');
  const [opening, setOpening] = useState<FormOpening>({
    initial: restoredKind !== null ? restored.value : pristine,
    unmatched: [],
    warnings: [],
    restoredAt: restored.savedAt,
    generation: 0,
  });

  function openForm(next: Omit<FormOpening, 'generation'>) {
    setOpening((current) => ({ ...next, generation: current.generation + 1 }));
    setMode('form');
  }

  function handleParsed(result: OnboardingParseRead) {
    openForm({
      initial: fromParsedFields(kind, result.fields),
      unmatched: result.unmatched,
      warnings: result.warnings,
      restoredAt: null,
    });
  }

  function startOver() {
    setOpening((current) => ({
      initial: pristine,
      unmatched: [],
      warnings: [],
      restoredAt: null,
      generation: current.generation + 1,
    }));
    setMode('kind');
  }

  if (mode === 'paste') {
    return <PasteBriefStep kind={kind} onParsed={handleParsed} onBack={() => setMode('choice')} />;
  }

  if (mode === 'form') {
    return (
      <BrandBriefForm
        key={opening.generation}
        flow={flow}
        initial={opening.initial}
        unmatched={opening.unmatched}
        warnings={opening.warnings}
        restoredAt={opening.restoredAt}
        draftKey={draftKey}
        onStartOver={startOver}
      />
    );
  }

  if (mode === 'choice') {
    return (
      <ChoiceStep
        onPaste={() => setMode('paste')}
        onManual={() =>
          openForm({
            initial: { ...pristine, kind },
            unmatched: [],
            warnings: [],
            restoredAt: null,
          })
        }
        onBack={() => setMode('kind')}
      />
    );
  }

  return (
    <KindStep
      isAdditional={flow === 'new-brand'}
      onPick={(picked) => {
        setKind(picked);
        setMode('choice');
      }}
      onBack={onCancel}
    />
  );
}

/**
 * First-run setup (SRS §2): what the content is for, then one screen of typing
 * either way.
 *
 * Nothing persists until the final POST /me/onboarding; in-progress answers
 * mirror to this user's own localStorage key so a refresh or a crash mid-setup
 * costs nothing, and are cleared the moment the real record is filed.
 */
export function OnboardingPage() {
  const { profile, onboardingStatus, isLoading } = useAccount();
  const { user } = useAuth();

  if (isLoading || onboardingStatus === 'loading') {
    return <FullPageStatus message="Loading your setup" />;
  }

  // Already onboarded users have nothing to do here.
  if (onboardingStatus === 'complete') {
    return <Navigate to="/" replace />;
  }

  // A profile that has answers but no completed flag (a bootstrap that failed
  // halfway) opens the form with what was saved rather than blank.
  const pristine: BrandBriefValues = profile
    ? {
        ...EMPTY_BRIEF,
        business_name: profile.business_name ?? '',
        industry: profile.industry ?? '',
        value_proposition: profile.value_proposition ?? '',
        target_audience: profile.target_audience ?? '',
        goals: profile.goals,
      }
    : EMPTY_BRIEF;

  return (
    <main className="page page--narrow">
      <PageHeader
        section="Setup"
        title="Set up your first brand"
        lede="Everything you enter here shapes the posts we write."
      />
      {/* Scoped to the signed-in user: another account on this machine never
          sees these answers. */}
      <BrandFlow
        flow="onboarding"
        pristine={pristine}
        draftKey={user ? onboardingDraftKey(user.id) : null}
      />
    </main>
  );
}

/**
 * One more brand on an onboarded account. The same flow as first-run setup,
 * posting to POST /me/businesses instead, with its own draft key so an
 * abandoned attempt here never reopens on the first-run screen.
 */
export function NewBrandPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // At the plan's brand allowance the setup flow would only end in a 402, so
  // the page says so before any typing. Never true while the plan loads.
  const allowance = useBrandAllowance();

  return (
    <main className="page page--narrow">
      <PageHeader
        section="Brands"
        title="Add a brand"
        lede="A second business, or your own personal brand beside it. Each has its own brand profile, plans and history."
      />
      {allowance.fields ? (
        <div className="upsell-empty">
          <Upsell fields={allowance.fields} />
        </div>
      ) : (
        <BrandFlow
          flow="new-brand"
          pristine={emptyBrief('business')}
          draftKey={user ? newBrandDraftKey(user.id) : null}
          onCancel={() => void navigate('/', { replace: true })}
        />
      )}
    </main>
  );
}
